import { Box, Chip, Icon, Link, Tooltip, Typography, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Check, ExitToApp, Remove, Warning } from '@material-ui/icons'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { getAllStaff } from '../../actions/Team/team.actions'
import { getVisitsByFacilityId } from '../../actions/visits/visits.actions'
import { ScheduledVisitsTable } from '../../components/Tables/ScheduledVisitsTable'
import { getChipStyles, getDogIssues } from '../../services/helpers'
import {
	STATUS_ID,
	checkIfNeedsRenewalAndShouldBeNotified,
	checkIfPetIsBanned,
	checkIfPetHasCompletedGrooming,
	getIconAndTagByFoundingAndMembership,
	sortByKey,
	GROOMING,
	BOARDING,
	DAYCARE,
	GROOMING_SERVICE_TYPES,
	GROOMING_SERVICE_LABELS,
	COMPATIBILITY_SESSION,
	VISIT_TYPE_DISPLAY_NAMES,
	ADDON_ONLY,
	ADMIN_ONLY
} from '../../utils'
import useQuery from '../../hooks/useQuery'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	},
	petStatusIcon: {
		position: 'absolute',
		left: 0
	},
	membershipWrapper: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center'
	},
	membershipIcon: {
		color: '#6B4AD6',
		'&::before': {
			fontSize: '14px',
			lineHeight: '14px'
		}
	},
	membershipType: {
		fontFamily: 'Nunito',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '19px',
		color: '#777679',
		textTransform: 'capitalize'
	}
}))

const GroomingSchedule = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const { getQueryParam, pushQueryParamToHistory, replaceQueryParamInHistory } = useQuery()
	const theme = useTheme()

	let aggregates = {
		total_appointments: {
			label: 'Total Appointments',
			value: 0
		},
		approved_appointments: {
			label: 'Approved Appointments',
			value: 0
		},
		unassigned_dogs: {
			label: 'Unassigned Dogs',
			value: 0
		},
		in_progress_appointments: {
			label: 'In Progress Appointments',
			value: 0
		},
		haircut_service: {
			label: GROOMING_SERVICE_LABELS[GROOMING_SERVICE_TYPES.haircut],
			value: 0
		},
		cleanup_service: {
			label: GROOMING_SERVICE_LABELS[GROOMING_SERVICE_TYPES.cleanup],
			value: 0
		},
		trim_service: {
			label: GROOMING_SERVICE_LABELS[GROOMING_SERVICE_TYPES.trim],
			value: 0
		},
		bath_service: {
			label: GROOMING_SERVICE_LABELS[GROOMING_SERVICE_TYPES.bath],
			value: 0
		}
		// nail_trim_service: {
		// 	label: GROOMING_SERVICE_LABELS[GROOMING_SERVICE_TYPES.nail_trim],
		// 	value: 0
		// }
	}

	// const invalidSexes = ['Intact Male', 'Intact Female']

	const facility = useSelector((state) => state.specific.facilities.selected)
	const visits = useSelector((state) => state.specific.visits.visits)
	const selectedFilters = useSelector((state) => state.specific.visits.selectedGroomingFilters)
	const staffList = useSelector((state) => state.specific.team.staffList)
	const [filteredVisits, setFilteredVisits] = useState([])

	const queryDate = getQueryParam('date')

	const [date, setDate] = useState()

	const [scheduleAggregates, setScheduleAggregates] = useState(aggregates)

	useEffect(() => {
		dispatch({ type: 'CLEAR_VISITS' })
		dispatch(getAllStaff())
	}, [])

	useEffect(() => {
		if (queryDate) {
			setDate(moment(queryDate).format('ll'))
		} else {
			setDate(moment().format('ll'))
			replaceQueryParamInHistory({ key: 'date', value: moment().format('YYYY-MM-DD') })
		}
	}, [queryDate])

	const getColumns = () => {
		return [
			{
				title: 'Assignment',
				render: (data) => renderAssignment(data, classes),
				customSort: (a, b) => {
					const aStartTime = moment(a.start_time || 0, 'HH:mm')
					const bStartTime = moment(b.start_time || 0, 'HH:mm')
					const diff = aStartTime.diff(bStartTime)
					return diff
				}
			},
			{
				width: 160,
				title: 'Details',
				render: (data) => renderDetails(data, classes),
				customSort: (a, b) => {
					const aDetail = getDetailName(a)
					const bDetail = getDetailName(b)

					return aDetail.localeCompare(bDetail)
				}
			},
			{
				title: 'Appointment',
				field: 'type',
				render: (data) => renderAppointment(data)
			},
			{
				title: 'Client',
				render: (data) => renderClientName(data),
				customFilterAndSearch: (value, rowData) => {
					let { household } = rowData
					let account = household.account || household.accounts.find((a) => a.primary)
					let name = value
					if (name) name = name.toUpperCase()
					return account ? account.full_name.toUpperCase().includes(name.toUpperCase()) : false
				},
				customSort: (a, b) => {
					if (a.household && b.household && a.household.accounts && b.household.accounts) {
						let accountA = a.household.accounts.find((a) => a.primary)
						let accountB = b.household.accounts.find((b) => b.primary)
						return accountA.full_name.localeCompare(accountB.full_name)
					}
				}
			},
			{
				title: 'Pet(s)',
				field: 'pets',
				render: (data) => renderPets(data, classes),
				customFilterAndSearch: (value, rowData) => {
					let { pets } = rowData
					let name = value
					if (name) name = name.toUpperCase()
					return pets && pets.length > 0
						? pets.findIndex((pet) => pet.name.toUpperCase().includes(name)) !== -1
						: false
				},
				customSort: (a, b) => {
					if (b.pets && b.pets.length > 0 && a.pets && a.pets.length > 0) {
						let petA = a.pets[0]
						let petB = b.pets[0]
						return petA.name.localeCompare(petB.name)
					}
				}
			},
			{
				title: 'Status',
				field: 'status.name',
				render: (data) => {
					return (
						<Box>
							<Chip
								label={data.status ? data.status.name : 'No Status'}
								style={data.status ? getChipStyles(data.status.name) : null}
							/>
							<Typography
								style={{
									fontWeight: '600',
									fontSize: 14,
									color: '#777679'
								}}>
								Service status: {data.grooming_status || 'Upcoming'}
							</Typography>
						</Box>
					)
				}
			},
			{
				title: 'Engagement',
				render: (data) => renderEngagement(data)
			}
		]
	}

	// Wait for the date (it starts unset) and send it as YYYY-MM-DD. Depend on the facility's
	// id, not the object, so a reloaded copy of the same facility doesn't refetch.
	useEffect(() => {
		if (facility?._id && date) {
			dispatch(getVisitsByFacilityId(facility, moment(date).format('YYYY-MM-DD')))
		}
	}, [date, facility?._id])

	const renderAssignment = (data) => {
		let staff = '--'
		if (
			staffList &&
			(data.staff_id || data?.grooming_related_addon?.staff_id || data?.standalone_addon?.staff_id)
		) {
			const foundStaff = staffList.find(
				(s) =>
					s._id ===
					(data.staff_id || data?.grooming_related_addon?.staff_id || data?.standalone_addon?.staff_id)
			)
			if (foundStaff) staff = `${foundStaff?.contact?.first_name} ${foundStaff?.contact?.last_name[0]}.`
		}

		return (
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'flex-start'
				}}>
				<Typography
					style={{
						fontWeight: '600',
						fontSize: 14,
						color: '#1a1a35'
					}}>
					{staff}
				</Typography>
				<Typography
					style={{
						color: '#777679',
						fontWeight: '600',
						fontSize: 14
					}}>
					{data.start_time ? moment(data.start_time, 'HH:mm').format('hh:mm a') : '--'}
				</Typography>
			</div>
		)
	}

	useEffect(() => {
		if (visits) {
			let grooms = visits.filter((v) => {
				if (v.type === GROOMING) {
					return true
				}
				return false
			})

			let grooming_related_addons = []
			const standalone_addons = []

			visits.forEach((v) => {
				if ([BOARDING, DAYCARE].includes(v.type)) {
					const addOns = v.add_on_services || []
					const groomingAddons = addOns.filter((a) => {
						return (
							a.revenue_type === GROOMING && a.date && a.date === moment(date).format('YYYY-MM-DD')
						)
					})

					groomingAddons.forEach((add) => {
						grooming_related_addons.push({
							...v,
							grooming_related_addon: add
						})
					})
				}
				if (v.type === ADDON_ONLY && v.subtype === GROOMING) {
					const addOns = v.add_on_services || []
					const groomingAddons = addOns.filter((a) => {
						return (
							a.revenue_type === GROOMING &&
							a.date &&
							a.date === moment(date).format('YYYY-MM-DD') &&
							a.standalone_service === true
						)
					})

					groomingAddons.forEach((add) => {
						standalone_addons.push({
							...v,
							standalone_addon: add
						})
					})
				}
			})

			let visitsFiltered = [...grooms, ...grooming_related_addons, ...standalone_addons].sort(
				sortByKey((v) => moment(v.start_time || 0, 'HH:mm'))
			)
			if (selectedFilters['service_type'] && selectedFilters['service_type'].length) {
				if (facility) {
					let groomingAddOns = facility.addOnTypes
						.filter((a) => a.revenue_type === GROOMING && !!a.active)
						.map((a) => a.name)
					visitsFiltered = visitsFiltered.filter((v) => {
						if (
							selectedFilters['service_type'].some((type) => groomingAddOns.includes(type)) &&
							[BOARDING, DAYCARE].includes(v.type)
						) {
							const selected_filters = selectedFilters['service_type']
							const hasSelectedGroomingRelatedAddon =
								v.grooming_related_addon && selected_filters.includes(v.grooming_related_addon)
							return hasSelectedGroomingRelatedAddon
						}
						return selectedFilters['service_type'].includes(v.grooming_service_type)
					})
				}
			}

			if (selectedFilters['appointment_status'] && selectedFilters['appointment_status'].length) {
				visitsFiltered = visitsFiltered.filter((v) =>
					v.status && v.status.id ? selectedFilters['appointment_status'].includes(v.status.id) : false
				)
			} else {
				// if no filter is selected we don't want to show cancelled
				visitsFiltered = visitsFiltered.filter(
					(v) => v.status && v.status.id && v.status.id !== STATUS_ID.Cancelled
				)
			}

			if (selectedFilters.hasAlerts) {
				visitsFiltered = visitsFiltered.filter((v) => {
					return v.pets.some((pet) => {
						let issues = getDogIssues(pet, v)

						return issues && issues.length ? true : false
					})
				})
			}

			setFilteredVisits(visitsFiltered)
		}
	}, [selectedFilters, visits])

	useEffect(() => {
		if (facility) {
			let groomingAddOns = facility.addOnTypes.filter((a) => a.revenue_type === GROOMING && !!a.active)
			let arr = []

			groomingAddOns.forEach((add) => {
				let find = arr.find((a) => a.name == add.name)
				if (!find) arr.push({ value: add.name, label: add.name })
			})

			dispatch({ type: 'SET_GROOMING_FILTERS', payload: arr })
		}
	}, [facility])

	useEffect(() => {
		let updatedAggregates = cloneDeep(scheduleAggregates)

		if (filteredVisits.length > 0) {
			updatedAggregates.total_appointments.value = filteredVisits.length
			updatedAggregates.approved_appointments.value = filteredVisits.filter((v) => v.status.id === 2).length
			updatedAggregates.unassigned_dogs.value = filteredVisits.filter((v) => !v.staff_id).length
			updatedAggregates.in_progress_appointments.value = filteredVisits.filter(
				(v) => v.status.id === 4
			).length
			updatedAggregates.haircut_service.value = filteredVisits.filter(
				(v) => v.grooming_service_type === 'haircut'
			).length
			updatedAggregates.cleanup_service.value = filteredVisits.filter(
				(v) => v.grooming_service_type === 'cleanup'
			).length
			updatedAggregates.trim_service.value = filteredVisits.filter(
				(v) => v.grooming_service_type === 'trim'
			).length
			updatedAggregates.bath_service.value = filteredVisits.filter(
				(v) => v.grooming_service_type === 'bath'
			).length

			if (facility) {
				// setting up the pet care enrichments that need to be done by grooming staff
				let groomingAddOns = facility.addOnTypes.filter((a) => a.revenue_type === GROOMING && !!a.active)

				// have to repeat this loop to avoid overwriting addons that have the same name
				groomingAddOns.forEach((add) => {
					updatedAggregates[add.name] = { value: 0, label: add.name }
				})

				groomingAddOns.forEach((add) => {
					let num = 0
					filteredVisits.forEach((v) => {
						if ([BOARDING, DAYCARE].includes(v.type)) {
							if (v?.grooming_related_addon?.addon_id == add._id) num += 1
						}
					})

					updatedAggregates[add.name] = updatedAggregates[add.name]
						? { ...updatedAggregates[add.name], value: updatedAggregates[add.name].value + num }
						: { value: num, label: add.name }
				})
			}

			setScheduleAggregates(updatedAggregates)
		} else {
			for (const key in updatedAggregates) {
				if (updatedAggregates.hasOwnProperty(key)) {
					if (typeof updatedAggregates[key] === 'object') {
						updatedAggregates[key].value = 0
					}
				}
			}

			setScheduleAggregates(updatedAggregates)
		}
	}, [filteredVisits])

	const changeDate = (type) => {
		if (type === 'add') {
			const forwardDate = moment(date).add(1, 'd')
			pushQueryParamToHistory({ key: 'date', value: forwardDate.format('YYYY-MM-DD') })
			setDate(forwardDate.format('ll'))
		} else if (type === 'subtract') {
			const backDate = moment(date).subtract(1, 'd')
			pushQueryParamToHistory({ key: 'date', value: backDate.format('YYYY-MM-DD') })
			setDate(backDate.format('ll'))
		}
	}

	const getDetailName = (data) => {
		let badStati = [5, 6, 7, 8]
		if (data?.status?.id && badStati.includes(data.status.id)) return 'blank'

		if (data?.status?.id === 4) return 'pickup'
		else return 'dropoff'
	}

	const renderDetails = (data) => {
		const detail = getDetailName(data)
		if (detail === 'blank') return null

		let pet_id = data?.pet_ids?.[0] || null
		let hasRelatedPetCareVisit = visits?.find(
			(v) => v.type !== GROOMING && v?.pet_ids?.includes(pet_id) && v.status.id !== STATUS_ID.Cancelled
		)

		const isAddOnGroomingVisitOnly = data.type === ADDON_ONLY && data.subtype === GROOMING
		if (isAddOnGroomingVisitOnly) {
			return (
				<Fragment>
					<Typography variant="caption">Drop Off</Typography>
					<Typography variant="caption" style={{ color: '#777679', display: 'block' }}>
						Walk-In
					</Typography>
				</Fragment>
			)
		}

		if (detail === 'pickup') {
			return (
				<Fragment>
					<Typography variant="caption">Pick Up</Typography>
					<Typography variant="caption" style={{ color: '#777679', display: 'block' }}>
						{hasRelatedPetCareVisit
							? 'Pet Care'
							: data.pickup_time
								? moment(data.pickup_time, 'HH:mm').format('hh:mm a')
								: '--'}
					</Typography>
				</Fragment>
			)
		}

		return (
			<Fragment>
				<Typography variant="caption">Drop Off</Typography>
				<Typography variant="caption" style={{ color: '#777679', display: 'block' }}>
					{hasRelatedPetCareVisit
						? 'Pet Care'
						: data.dropoff_time
							? moment(data.dropoff_time, 'HH:mm').format('hh:mm a')
							: '--'}
				</Typography>
			</Fragment>
		)
	}

	const renderPets = (data, classes) => {
		const { status } = data
		const isCompleted = status ? STATUS_ID.Completed === status.id : false
		return (
			<div>
				{data.pets && data.pets.length > 0
					? data.pets.map((pet, index) => {
						let issues = getDogIssues(pet, data)
						return (
							<div key={`pet-${index}`}>
								<div
									key={pet._id}
									style={{
										display: 'flex',
										alignItems: 'center',
										position: 'relative',
										justifyContent: 'space-between',
										paddingLeft: 32
									}}>
									{pet.status && pet.status.checked_in ? (
										<Check
											className={classes.petStatusIcon}
											style={{
												fill: '#4CAF50',
												height: 16,
												strokeWidth: 3,
												stroke: '#4CAF50'
											}}
										/>
									) : pet.status && pet.status.checked_in === false && isCompleted ? (
										<ExitToApp
											className={classes.petStatusIcon}
											style={{
												fill: '#777679',
												height: 16,
												strokeWidth: 1,
												stroke: '#777679'
											}}
										/>
									) : (
										<Remove
											className={classes.petStatusIcon}
											style={{
												fill: '#777679',
												height: 16,
												strokeWidth: 1,
												stroke: '#777679'
											}}
										/>
									)}
									<Link
										component={RouterLink}
										style={{
											color: '#1A1A35'
										}}
										onClick={(e) => e.stopPropagation()}
										to={`/clients/${data.household._id}/pets/${pet._id}`}>
										{pet.name + ' '}{' '}
										{pet.sex
											? pet.sex == 'Intact Male' || pet.sex == 'Neutered Male'
												? '(M)'
												: pet.sex == 'Intact Female' || pet.sex == 'Spayed Female'
													? '(F)'
													: ''
											: ''}
									</Link>
									<div style={{ marginLeft: '15px' }}>
										{issues && issues.length ? (
											<Tooltip title={renderToolTip(issues)}>
												<Chip
													label={<Warning style={{ fontSize: '15px' }} />}
													style={
														issues.some((i) => i.type === 'danger')
															? {
																...getChipStyles('rejected'),
																...{ paddingTop: '6px' }
															}
															: {
																...getChipStyles('warning'),
																...{ paddingTop: '6px' }
															}
													}
												/>
											</Tooltip>
										) : null}
									</div>
								</div>
							</div>
						)
					})
					: null}
			</div>
		)
	}

	const renderToolTip = (issues) => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{issues.map((i, index) => (
					<ToolTipItem key={`tooltip-${index}`} message={i.message} color={i.color || null} />
				))}
			</div>
		)
	}

	const ToolTipItem = (props) => {
		let { message, color } = props
		return (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<div
					style={{
						height: '9px',
						width: '9px',
						backgroundColor: `${color ? color : '#EB4C4C'}`,
						borderRadius: '50%',
						display: 'inline-block',
						marginRight: '16px'
					}}
				/>
				<Typography style={{ fontWeight: 600, fontSize: '14px' }}>{message}</Typography>
			</div>
		)
	}

	const handleAppRightClick = (e, prms) => {
		e.preventDefault()
		const url = `/clients/${prms.household_id}/appointments/${prms._id}${`?date=${prms.date}`}`
		const win = window.open(url, '_blank')
		win.focus()
	}

	const renderAppointment = (data) => {
		const { type, grooming_service_type, add_on_services = [], grooming_related_addon, needs_grooming_consultation } = data
		let service_type

		if (grooming_service_type) {
			const groomingServices = facility?.services?.grooming?.services || []
			const type = groomingServices.find((gs) => gs.type === grooming_service_type)
			if (type) service_type = type.name
		}

		if ([BOARDING, DAYCARE].includes(type)) {
			service_type = grooming_related_addon?.name || ''
		}

		const formattedDate = moment(date).format('YYYYMMDD')
		const appPrms = {
			_id: data._id,
			household_id: data.household_id,
			date: formattedDate
		}
		return (
			<div>
				<Typography
					onContextMenu={(e) => handleAppRightClick(e, appPrms)}
					style={{
						textTransform: 'capitalize',
						color: '#6B4AD6',
						fontWeight: 800,
						fontSize: '16px',
						lineHeight: '22px'
					}}>
					{type === COMPATIBILITY_SESSION ? 'Compatibility Session' : VISIT_TYPE_DISPLAY_NAMES[type]} {needs_grooming_consultation ? ' - NEW' : null}
				</Typography>
				<Typography variant="body2" style={{ textTransform: 'capitalize' }}>
					{service_type}
				</Typography>

			</div>
		)
	}

	const renderClientName = (data) => {
		if (data.household && data.household.accounts) {
			const account = data.household.accounts.find((a) => a.primary)
			const [iconName, iconColor, tag] = getIconAndTagByFoundingAndMembership(data.household)

			return (
				<div>
					<Link
						component={RouterLink}
						style={{
							color: theme.palette.secondary.main,
							marginBottom: 5,
							fontSize: '14px',
							lineHeight: '19px'
						}}
						onClick={(e) => e.stopPropagation()}
						to={`/clients/${data.household._id}/contacts`}>
						{account ? account.full_name : 'No name'}
					</Link>
					{!!iconName || !!tag ? (
						<div className={classes.membershipWrapper}>
							{!!iconName && (
								<Icon
									className={`fa fa-${iconName}`}
									style={{
										color: iconColor,
										fontWeight: '900',
										fontSize: '14px',
										lineHeight: '16px',
										width: 20
									}}
								/>
							)}
							<Typography className={classes.membershipType}>{tag}</Typography>
						</div>
					) : null}
				</div>
			)
		}

		return 'No client'
	}

	const renderEngagement = (visit) => {
		let bannedFromBarAndPark = false
		let bannedFromPetCare = false
		let hasCompletedGrooming = false
		let bannedFromGrooming = false
		const [needsRenewal, shouldBeNotified] = checkIfNeedsRenewalAndShouldBeNotified(
			visit?.household,
			visit?.household?.memberships
		)
		if (visit?.pets && visit?.pets?.length) {
			bannedFromBarAndPark =
				visit && visit.pets && visit.pets.some((p) => checkIfPetIsBanned(p, 'Bar & Park'))
			bannedFromPetCare =
				visit && visit.pets && visit.pets.some((p) => checkIfPetIsBanned(p, 'Daycare & Boarding'))
			hasCompletedGrooming = visit && visit.pets && visit.pets.some((p) => checkIfPetHasCompletedGrooming(p))
			bannedFromGrooming = visit && visit.pets && visit.pets.some((p) => checkIfPetIsBanned(p, 'Grooming'))
		}

		const getIconColor = (banned) => (banned ? '#DB4A42' : '#007A4B')
		const getTextColor = (banned) => (banned ? '#DC2A2A' : '#007A4B')

		return (
			<Fragment>
				{(visit?.household && !visit?.household?.hasUsedBarAndPark) || bannedFromBarAndPark ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${bannedFromBarAndPark ? 'times' : 'seedling'}`}
							style={{
								color: getIconColor(bannedFromBarAndPark),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromBarAndPark),
								width: '100%',
								display: 'block'
							}}>
							Bar & Park
						</Typography>
					</div>
				) : null}
				{(visit?.household && !visit?.household?.hasUsedPetCare) || bannedFromPetCare ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${bannedFromPetCare ? 'times' : 'seedling'}`}
							style={{
								color: getIconColor(bannedFromPetCare),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromPetCare),
								width: '100%',
								display: 'block'
							}}>
							Pet Care
						</Typography>
					</div>
				) : null}
				{!hasCompletedGrooming ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-seedling`}
							style={{
								color: '#007A4B',
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: '#007A4B',
								width: '100%',
								display: 'block'
							}}>
							Grooming
						</Typography>
					</div>
				) : null}
				{bannedFromGrooming ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-times`}
							style={{
								color: getIconColor(bannedFromGrooming),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromGrooming),
								width: '100%',
								display: 'block'
							}}>
							Grooming
						</Typography>
					</div>
				) : null}

				{needsRenewal && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${shouldBeNotified ? 'bell' : 'bell-slash'}`}
							style={{
								color: shouldBeNotified ? '#0074C8' : '#A2A1A4',
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: shouldBeNotified ? '#0074C8' : '#A2A1A4',
								width: '100%',
								display: 'block'
							}}>
							Needs Renewal
						</Typography>
					</div>
				)}
			</Fragment>
		)
	}

	return (
		<div className={classes.root}>
			{facility ? (
				<div className={classes.visitsTableContainer}>
					<ScheduledVisitsTable
						data={filteredVisits}
						grooming={true}
						columns={getColumns()}
						scheduleAggregates={scheduleAggregates}
						date={date}
						changeDate={changeDate}
						setDate={setDate}
					/>
				</div>
			) : (
				<h3>Please Select a Facility</h3>
			)}
		</div>
	)
}

export default GroomingSchedule
